/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable complexity */
/* eslint-disable consistent-return */
import React, { ReactNode, useCallback, useEffect } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { envConfigs } from '../config/envConfigs';
import { NOT_APPROVED_PATH, NOT_AUTHORISED_PATH } from '../utils/paths';
import { log } from '../utils/logger';

import { requestInterceptor } from './requestInterceptors';
import { useToast } from '../contexts/ToastContext/ToastContext';
import { useCustomerSelection } from '../contexts/CustomerSelectionContext/CustomerSelectionContext';
import { getAccountIdStorageKey } from '../utils/authProvider';

declare module 'axios' {
  export interface AxiosRequestConfig {
    hideGlobalError?: boolean;
    retriedAfterAccountIdClear?: boolean;
  }
}

const http = axios.create({
  baseURL: envConfigs.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

http.interceptors.request.use(
  async config => {
    return requestInterceptor(config);
  },
  async error => {
    return Promise.reject(error);
  }
);

export const handleResponse = <T,>(response: AxiosResponse<T>): T => {
  return response.data;
};

interface Props {
  children: ReactNode;
}

export const WithAxios: React.FC<Props> = ({ children }: Props) => {
  const { showToast } = useToast();
  const { requireSelection } = useCustomerSelection();

  const handleError = useCallback(
    (error: AxiosError): Promise<AxiosResponse> | void => {
      if (error.response !== undefined) {
        log('error response', error.response);

        if (error.config?.hideGlobalError) {
          throw error;
        }

        if (error.response.status === 401) {
          return;
        }

        if (error.response.status === 400) {
          const responseData = error.response.data as any;
          if (responseData?.errorCode === 'ACCOUNT_ID_NOT_ALLOWED') {
            const storageKey = getAccountIdStorageKey();
            if (storageKey) localStorage.removeItem(storageKey);

            if (!error.config?.retriedAfterAccountIdClear) {
              return http.request({
                ...error.config,
                retriedAfterAccountIdClear: true,
              });
            }

            showToast();
            throw error;
          }
        }

        if (error.response.status === 428) {
          const responseData = error.response.data as any;
          if (responseData?.errorCode === 'CUSTOMER_SELECTION_REQUIRED') {
            requireSelection(responseData.customers || []);
            throw error;
          }
        }

        if (error.response.status === 403) {
          if ((error.response.data as any).detail.includes('not approved')) {
            window.location.href = NOT_APPROVED_PATH;
          } else {
            window.location.href = NOT_AUTHORISED_PATH;
          }
        } else {
          showToast();
        }
      } else {
        showToast();
        log(error.message);
      }
      throw error;
    },
    [showToast, requireSelection]
  );

  useEffect(() => {
    const id = http.interceptors.response.use(handleResponse, handleError);
    return () => {
      http.interceptors.response.eject(id);
    };
  }, [handleError]);

  return <>{children}</>;
};

export default http;
